import { useCallback, useMemo } from 'react';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { useEmployee } from '~/data/employee';
import {
  AllowanceProposalProps,
  BenefitProposalProps,
  BillableHoursProposalProps,
  BonusProposalProps,
  ProposalAgent,
  ProposalPreviewProps,
  ProposalProps,
  ProposalStatus,
  ProposalType,
  SalaryProposalProps,
} from '~/data/proposals';
import { useGetProposalPreview } from '~/data/proposals/useGetProposalPreview';
import { useResponsibleHRSelect } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { isRoleHRAdmin } from '~/services/Permissions';

import { useNewEmployeeProposalInitiators } from '../EmployeeProposal/components/Forms/Common/useNewEmployeeProposalInitiators';

type PropsType = {
  employeeId: number;
  proposalType: ProposalType;
};

type UseNewProposalOutput = {
  proposalPreview?: ProposalPreviewProps;
  newProposal?: ProposalProps;
  isLoading: boolean;
  user: {
    isInitiator: boolean;
  };
};

export const useNewEmployeeProposal = (
  props: PropsType,
): UseNewProposalOutput => {
  const { employeeId, proposalType } = props;

  const { employee, isLoading: isLoadingUseEmployee } = useEmployee();

  const defaultEmployeeInitiators = useNewEmployeeProposalInitiators(
    employee,
    isLoadingUseEmployee,
  );

  const loggedInEmployeeId = useLoggedInEmployeeId();
  const loggedInUserRoles = useLoggedInUserRoles();
  const isHRAdmin = isRoleHRAdmin(loggedInUserRoles);

  const { data: proposalPreview, isLoading: isLoadingProposalPreview } =
    useGetProposalPreview({
      targetId: employeeId,
      proposalType,
    });

  const { allResponsibleHRIds, isLoading: isLoadingResponsibleHR } =
    useResponsibleHRSelect(employeeId);

  const isLoading = isLoadingProposalPreview || isLoadingResponsibleHR;

  const isInitiator = useMemo(() => {
    if (isHRAdmin) {
      return true;
    }

    const getAgentUserIds = (agents?: ProposalAgent[]): number[] =>
      !!agents ? agents.map((a) => a.userId) : [];

    const configInitiatorIds = getAgentUserIds(
      proposalPreview?.permissions.initiators,
    );
    const configAdministrators = getAgentUserIds(
      proposalPreview?.administrators,
    );

    const initiatorIds = [
      ...configInitiatorIds,
      ...configAdministrators,
      ...allResponsibleHRIds,
    ];

    return [...defaultEmployeeInitiators, ...initiatorIds].includes(
      loggedInEmployeeId,
    );
  }, [
    loggedInEmployeeId,
    isHRAdmin,
    proposalPreview,
    allResponsibleHRIds,
    defaultEmployeeInitiators,
  ]);

  // Resolve new proposal data
  const getNewProposal = useCallback(
    (proposalPreview?: ProposalPreviewProps): ProposalProps | undefined => {
      const commonNewProposalProps: Omit<ProposalProps, 'proposalData'> = {
        proposalType,
        proposalId: '',
        approvalSteps: proposalPreview?.approvalSteps || [],
        initiator: proposalPreview?.initiator || {
          userId: loggedInEmployeeId,
          roleName: 'INITIATOR',
        },
        createdTime: new Date(),
        updatedTime: new Date(),
        targetId: proposalPreview?.targetId || employeeId,
        overseers: proposalPreview?.overseers || [],
        administrators: proposalPreview?.administrators || [],
        proposalStatus: ProposalStatus.PROPOSED,
      };

      switch (proposalType) {
        case ProposalType.SALARY_PROPOSAL:
          return {
            ...commonNewProposalProps,
            proposalData: {},
          } as SalaryProposalProps;
        case ProposalType.BENEFIT_PROPOSAL:
          return {
            ...commonNewProposalProps,
            proposalData: {},
          } as BenefitProposalProps;
        case ProposalType.BONUS_PROPOSAL:
          return {
            ...commonNewProposalProps,
            proposalData: {},
          } as BonusProposalProps;
        case ProposalType.ALLOWANCE_PROPOSAL:
          return {
            ...commonNewProposalProps,
            proposalData: {},
          } as AllowanceProposalProps;
        case ProposalType.BILLABLE_HOURS:
          return {
            ...commonNewProposalProps,
            proposalData: {},
          } as BillableHoursProposalProps;
        default:
          return undefined;
      }
    },
    [employeeId, loggedInEmployeeId, proposalType],
  );

  const newProposal = useMemo(() => {
    return getNewProposal(proposalPreview);
  }, [getNewProposal, proposalPreview]);

  return {
    proposalPreview,
    newProposal,
    isLoading,
    user: {
      isInitiator,
    },
  };
};
